import { DATA_CONSUMED_KEY, DATA_GENERATED_KEY } from 'src/util/constants';
import logger from 'src/util/logger';

import { getPortfolioDataInBatches } from './dataBatching';

// ── Constants ─────────────────────────────────────────────────────
export const MAX_CACHE_SEGMENTS = 5;
const ADJACENCY_TOLERANCE_SECONDS = 1;

/**
 * Build a composite cache key from historian and trade aggregation levels.
 * Segments cached at one trade granularity must not be served when
 * tradeAggregation differs, even if historianAggregation matches.
 * @param {string} historianAggregation - Aggregation for energy data.
 * @param {string} tradeAggregation - Aggregation for trade data.
 * @param {string} timezone - Timezone string (affects P1D bucket boundaries).
 * @returns {string} Composite key for cache segment matching.
 */
export const buildCacheAggregationKey = (historianAggregation, tradeAggregation, timezone) => (
  `${historianAggregation}|${tradeAggregation}|${timezone}`
);

// ── Module-level singleton cache ──────────────────────────────────
/**
 * Build a fresh cache object for a portfolio.
 * @param {string} portfolioId - Portfolio UUID to scope the cache.
 * @returns {object} Fresh cache object.
 */
export const buildInitialCache = (portfolioId) => ({
  portfolioId,
  segments: {},
});

let cache = buildInitialCache(null);

// ── Segment Lookup ────────────────────────────────────────────────
/**
 * Find a cached segment whose range fully covers the requested dateSpan
 * and matches the aggregation level.
 * @param {object} segments - Cache segments keyed by `"startSec_finishSec"`.
 * @param {object} dateSpan - Requested range with `start` and `finish` Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level (e.g. 'PT30M', 'P1D').
 * @returns {object|null} Matching segment or null.
 */
export const getSegmentDataFromCache = (segments, dateSpan, aggregation) => {
  if (!segments || !dateSpan?.start || !dateSpan?.finish) return null;
  const requestedStart = Math.floor(dateSpan.start.toSeconds());
  const requestedFinish = Math.floor(dateSpan.finish.toSeconds());

  const keys = Object.keys(segments);
  for (let i = 0; i < keys.length; i += 1) {
    const segment = segments[keys[i]];
    if (segment.aggregation === aggregation) {
      const segStart = Math.floor(segment.start.toSeconds());
      const segFinish = Math.floor(segment.finish.toSeconds());
      if (segStart <= requestedStart && segFinish >= requestedFinish) {
        return segment;
      }
    }
  }
  return null;
};

// ── Extract (filter to requested range) ───────────────────────────
/**
 * Extract a subset of combinedData filtered to the requested dateSpan.
 * Filters per-meter time-series and portfolio-level tradeSetSummary
 * without mutating the source.
 * @param {object} combinedData - Cached combinedData from a segment.
 * @param {object} dateSpan - Requested range with `start` and `finish` Luxon DateTimes.
 * @returns {object} New combinedData scoped to the requested range.
 */
export const extractDataFromCache = (combinedData, dateSpan) => {
  if (!combinedData?.portfolio?.meters?.edges || !dateSpan?.start || !dateSpan?.finish) {
    return combinedData;
  }

  const startSeconds = Math.floor(dateSpan.start.toSeconds());
  const finishSeconds = Math.floor(dateSpan.finish.toSeconds());

  const filterTimeSeries = (dataObj) => {
    if (!dataObj?.data) return dataObj;
    return {
      ...dataObj,
      data: dataObj.data.filter(
        (point) => point.timestamp >= startSeconds && point.timestamp <= finishSeconds,
      ),
    };
  };

  const filterTradeSummaryData = (dataArray) => {
    if (!Array.isArray(dataArray)) return dataArray;
    return dataArray.filter(
      (point) => point?.range?.finish >= startSeconds
        && point?.range?.finish <= finishSeconds,
    );
  };

  const filterTradeSummary = (summaryArray) => {
    if (!Array.isArray(summaryArray)) return summaryArray;
    return summaryArray.map((trade) => {
      if (!Array.isArray(trade?.data)) return trade;
      return {
        ...trade,
        data: filterTradeSummaryData(trade.data),
      };
    });
  };

  const filteredEdges = combinedData.portfolio.meters.edges.map((edge) => {
    const node = edge?.node;
    if (!node) return edge;
    return {
      ...edge,
      node: {
        ...node,
        [DATA_CONSUMED_KEY]: filterTimeSeries(node[DATA_CONSUMED_KEY]),
        [DATA_GENERATED_KEY]: filterTimeSeries(node[DATA_GENERATED_KEY]),
        tradeSetSummary: filterTradeSummary(node.tradeSetSummary),
      },
    };
  });

  // Filter portfolio-level tradeSetSummary
  const filteredTradeSummary = Array.isArray(combinedData.portfolio.tradeSetSummary)
    ? combinedData.portfolio.tradeSetSummary.map((trade) => ({
      ...trade,
      data: filterTradeSummaryData(trade.data),
    }))
    : combinedData.portfolio.tradeSetSummary;

  return {
    ...combinedData,
    portfolio: {
      ...combinedData.portfolio,
      meters: {
        ...combinedData.portfolio.meters,
        edges: filteredEdges,
      },
      tradeSetSummary: filteredTradeSummary,
    },
  };
};

// ── Merge Helpers ─────────────────────────────────────────────────
/**
 * Build a composite key for trade deduplication.
 * @param {object} tradeKey - Trade key with tradePointId, ruleId, direction, type.
 * @returns {string} Composite key string.
 */
export const buildTradeKey = (tradeKey) => {
  if (!tradeKey) return '';
  const {
    tradePointId, ruleId, direction, type,
  } = tradeKey;
  return `${tradePointId || ''}_${ruleId || ''}_${direction || ''}_${type || ''}`;
};

/**
 * Merge two time-series arrays, deduplicating by timestamp.
 * Newer entries (from latestData) win on conflict.
 * @param {Array} existingData - Existing time-series data points.
 * @param {Array} latestData - Newer time-series data points to merge.
 * @returns {Array} Merged, deduplicated data sorted by timestamp.
 */
export const mergeUniqueTimeSeries = (existingData, latestData) => {
  const existing = Array.isArray(existingData) ? existingData : [];
  const latest = Array.isArray(latestData) ? latestData : [];
  if (existing.length === 0) return latest;
  if (latest.length === 0) return existing;

  const byTimestamp = new Map();
  existing.forEach((point) => { byTimestamp.set(point.timestamp, point); });
  latest.forEach((point) => { byTimestamp.set(point.timestamp, point); }); // newer wins
  return Array.from(byTimestamp.values()).sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Merge two trade summary data arrays, deduplicating by range.finish.
 * @param {Array} existingData - Existing trade summary data points.
 * @param {Array} latestData - Newer trade summary data points to merge.
 * @returns {Array} Merged, deduplicated data.
 */
export const mergeUniqueTradeSummaryData = (existingData, latestData) => {
  const existing = Array.isArray(existingData) ? existingData : [];
  const latest = Array.isArray(latestData) ? latestData : [];
  if (existing.length === 0) return latest;
  if (latest.length === 0) return existing;

  const byFinish = new Map();
  existing.forEach((point) => {
    if (point?.range?.finish) byFinish.set(point.range.finish, point);
  });
  latest.forEach((point) => {
    if (point?.range?.finish) byFinish.set(point.range.finish, point);
  });
  return Array.from(byFinish.values())
    .sort((a, b) => (a?.range?.finish ?? 0) - (b?.range?.finish ?? 0));
};

/**
 * Merge two combinedData objects (portfolio shape).
 * Merges per-meter consumed, generated, rules, and tradeSetSummary.
 * @param {object} existingData - Existing combinedData.
 * @param {object} latestData - Newer combinedData to merge in.
 * @returns {object} New combinedData with merged data.
 */
export const mergeCombinedData = (existingData, latestData) => {
  const existingEdges = existingData?.portfolio?.meters?.edges;
  if (!existingEdges) return latestData;

  const latestEdges = latestData?.portfolio?.meters?.edges;
  if (!latestEdges) return existingData;

  const latestByUuid = new Map();
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid) latestByUuid.set(edge.node.uuid, edge.node);
  });

  const matchedUuids = new Set();

  const mergedEdges = existingEdges.map((edge) => {
    const existingNode = edge?.node;
    if (!existingNode?.uuid) return edge;

    const latestNode = latestByUuid.get(existingNode.uuid);
    if (!latestNode) return edge;

    matchedUuids.add(existingNode.uuid);

    const mergedConsumed = {
      ...(existingNode[DATA_CONSUMED_KEY] || {}),
      ...(latestNode[DATA_CONSUMED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_CONSUMED_KEY]?.data,
        latestNode[DATA_CONSUMED_KEY]?.data,
      ),
    };

    const mergedGenerated = {
      ...(existingNode[DATA_GENERATED_KEY] || {}),
      ...(latestNode[DATA_GENERATED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_GENERATED_KEY]?.data,
        latestNode[DATA_GENERATED_KEY]?.data,
      ),
    };

    // Merge per-meter tradeSetSummary
    // Use an index map to avoid O(n) indexOf lookups per trade key.
    const mergedTrades = [...(existingNode.tradeSetSummary || [])];
    const tradeIndexMap = new Map();
    mergedTrades.forEach((trade, idx) => {
      tradeIndexMap.set(buildTradeKey(trade.key), idx);
    });

    (latestNode.tradeSetSummary || []).forEach((trade) => {
      const key = buildTradeKey(trade.key);
      const existingIdx = tradeIndexMap.get(key);
      if (existingIdx !== undefined) {
        mergedTrades[existingIdx] = {
          ...mergedTrades[existingIdx],
          data: mergeUniqueTradeSummaryData(mergedTrades[existingIdx].data, trade.data),
        };
      } else {
        tradeIndexMap.set(key, mergedTrades.length);
        mergedTrades.push(trade);
      }
    });

    // Merge rules (dedup by uuid)
    const mergedRulesEdges = [...(existingNode.rules?.edges || [])];
    const existingRuleIds = new Set(
      mergedRulesEdges.map((e) => e?.node?.uuid).filter(Boolean),
    );
    (latestNode.rules?.edges || []).forEach((ruleEdge) => {
      if (ruleEdge?.node?.uuid && !existingRuleIds.has(ruleEdge.node.uuid)) {
        mergedRulesEdges.push(ruleEdge);
      }
    });

    return {
      ...edge,
      node: {
        ...existingNode,
        ...latestNode,
        [DATA_CONSUMED_KEY]: mergedConsumed,
        [DATA_GENERATED_KEY]: mergedGenerated,
        tradeSetSummary: mergedTrades,
        rules: { edges: mergedRulesEdges },
      },
    };
  });

  // Append meters that exist only in latestData
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid && !matchedUuids.has(edge.node.uuid)) {
      mergedEdges.push(edge);
    }
  });

  // Merge portfolio-level tradeSetSummary
  // Use an index map to avoid O(n) indexOf lookups per trade key.
  const mergedPortfolioTrades = [...(existingData.portfolio?.tradeSetSummary || [])];
  const portfolioTradeIndexMap = new Map();
  mergedPortfolioTrades.forEach((trade, idx) => {
    portfolioTradeIndexMap.set(buildTradeKey(trade.key), idx);
  });

  (latestData.portfolio?.tradeSetSummary || []).forEach((trade) => {
    const key = buildTradeKey(trade.key);
    const existingIdx = portfolioTradeIndexMap.get(key);
    if (existingIdx !== undefined) {
      mergedPortfolioTrades[existingIdx] = {
        ...mergedPortfolioTrades[existingIdx],
        data: mergeUniqueTradeSummaryData(mergedPortfolioTrades[existingIdx].data, trade.data),
      };
    } else {
      portfolioTradeIndexMap.set(key, mergedPortfolioTrades.length);
      mergedPortfolioTrades.push(trade);
    }
  });

  return {
    ...existingData,
    portfolio: {
      ...existingData.portfolio,
      ...latestData.portfolio,
      meters: {
        ...existingData.portfolio.meters,
        edges: mergedEdges,
      },
      tradeSetSummary: mergedPortfolioTrades,
    },
  };
};

// ── Metadata helpers ──────────────────────────────────────────────
/**
 * Compute loading metadata from cached combinedData for overlay display.
 * @param {object} combinedData - Cached combinedData object.
 * @returns {object} { metersCount, rulesCount }
 */
export const computeCacheMetadata = (combinedData) => {
  const edges = combinedData?.portfolio?.meters?.edges || [];
  const metersCount = edges.length;
  let rulesCount = 0;
  edges.forEach((edge) => {
    rulesCount += edge?.node?.rules?.edges?.length || 0;
  });
  return { metersCount, rulesCount };
};

// ── Segment Management ────────────────────────────────────────────
/**
 * Add a new segment to the cache, merging with adjacent segments if possible.
 * Evicts the oldest segment when MAX_CACHE_SEGMENTS is exceeded.
 * @param {object} dateSpan - Range with `start` and `finish` Luxon DateTimes.
 * @param {object} combinedData - The merged data for this date range.
 * @param {string} aggregation - Aggregation level for this segment.
 */
export const addSegmentToCache = (dateSpan, combinedData, aggregation) => {
  if (!dateSpan?.start || !dateSpan?.finish) return;

  let mergedData = combinedData;
  let mergedStart = dateSpan.start;
  let mergedFinish = dateSpan.finish;

  // Chain-merge: after absorbing one adjacent segment the expanded range
  // may now touch another, so restart the scan until no more merges occur.
  let merged = true;
  while (merged) {
    merged = false;
    const keys = Object.keys(cache.segments);
    for (let i = 0; i < keys.length; i += 1) {
      const seg = cache.segments[keys[i]];
      if (seg.aggregation !== aggregation) {
        // Different aggregation — skip
      } else {
        const segStartSec = Math.floor(seg.start.toSeconds());
        const segFinishSec = Math.floor(seg.finish.toSeconds());
        const curStartSec = Math.floor(mergedStart.toSeconds());
        const curFinishSec = Math.floor(mergedFinish.toSeconds());

        const isAdjacent = Math.abs(segFinishSec - curStartSec) <= ADJACENCY_TOLERANCE_SECONDS
          || Math.abs(curFinishSec - segStartSec) <= ADJACENCY_TOLERANCE_SECONDS;
        const isOverlapping = segStartSec <= curFinishSec && segFinishSec >= curStartSec;

        if (isAdjacent || isOverlapping) {
          mergedData = mergeCombinedData(seg.data, mergedData);
          mergedStart = seg.start.toSeconds() < mergedStart.toSeconds()
            ? seg.start : mergedStart;
          mergedFinish = seg.finish.toSeconds() > mergedFinish.toSeconds()
            ? seg.finish : mergedFinish;
          delete cache.segments[keys[i]];
          merged = true;
          break; // restart scan after mutation
        }
      }
    }
  }

  const mergedStartSec = Math.floor(mergedStart.toSeconds());
  const mergedFinishSec = Math.floor(mergedFinish.toSeconds());
  const mergedKey = `${mergedStartSec}_${mergedFinishSec}`;

  cache.segments[mergedKey] = {
    start: mergedStart,
    finish: mergedFinish,
    data: mergedData,
    aggregation,
    createdAt: Date.now(),
  };

  // FIFO eviction
  const segKeys = Object.keys(cache.segments);
  if (segKeys.length > MAX_CACHE_SEGMENTS) {
    const oldest = segKeys.reduce((a, b) => (
      cache.segments[a].createdAt < cache.segments[b].createdAt ? a : b
    ));
    delete cache.segments[oldest];
  }
};

/**
 * Look up the cache for the current portfolioId. Returns the cache if
 * it matches; otherwise reinitialises for the new portfolio.
 * @param {string} portfolioId - Portfolio UUID.
 * @returns {object} The cache object for this portfolio.
 */
export const lookupCache = (portfolioId) => {
  if (cache.portfolioId !== portfolioId) {
    cache = buildInitialCache(portfolioId);
  }
  return cache;
};

/**
 * Clear the cache (for unmount or portfolio change).
 */
export const clearCache = () => {
  cache = buildInitialCache(null);
};

// ── Read-through cache layer ──────────────────────────────────────
/**
 * Attempt to serve the requested dateSpan from cache. On cache hit,
 * returns the extracted data immediately. On cache miss, calls
 * getPortfolioDataInBatches (full 5-stage pipeline including meters)
 * and caches the result — matching Property Show's pattern.
 * @param {object} args
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.dateSpan - { start, finish } Luxon DateTimes
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data
 * @param {Function} [args.dispatchFunc] - Reducer dispatch
 * @param {object}   [args.cancelledRef] - Cancellation token
 * @param {Function} [args.onDataReceivedFunc] - Called with
 *   (data, fromCache) on both hit and miss.
 * @param {Function} [args.onBeforeFetchFunc] - Called on cache miss
 *   before the fetch starts. Receives ({ segmentsExist }).
 * @param {Function} [args.onErrorFunc] - Error callback
 * @param {object}   [args.isFetchingRef] - Fetch guard ref
 */
export const getPortfolioDataFromCache = async (args) => {
  const {
    portfolioId, dateSpan, timezone, environment,
    historianAggregation, tradeAggregation, initialData,
    dispatchFunc, cancelledRef, onDataReceivedFunc, onBeforeFetchFunc,
    onErrorFunc, isFetchingRef,
  } = args;

  const currentCache = lookupCache(portfolioId);
  const cacheKey = buildCacheAggregationKey(historianAggregation, tradeAggregation, timezone);
  const segment = getSegmentDataFromCache(currentCache.segments, dateSpan, cacheKey);

  if (segment) {
    logger.log('Cache HIT — serving from cache');
    const extracted = extractDataFromCache(segment.data, dateSpan);

    if (onDataReceivedFunc) {
      onDataReceivedFunc(extracted, true);
    }
    return;
  }

  logger.log('Cache MISS — fetching from network');

  const segmentsExist = Object.keys(currentCache.segments).length > 0;

  if (onBeforeFetchFunc) {
    onBeforeFetchFunc({ segmentsExist });
  }

  await getPortfolioDataInBatches({
    portfolioId,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    dispatchFunc,
    cancelledRef,
    isFetchingRef,
    onDataReceived: (mergedData) => {
      // Cache the fetched data
      addSegmentToCache(dateSpan, mergedData, cacheKey);
      if (onDataReceivedFunc) {
        onDataReceivedFunc(mergedData, false);
      }
    },
    onError: onErrorFunc,
  });
};
